import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Pencil, X } from 'lucide-react';
import { useUpdateSubmission } from '@/hooks/useSubmissions';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { SubmissionThumbnail } from '@/components/submissions/SubmissionThumbnail';
import { SubmissionStatus, type Submission } from '@/api/types';
import { submissionStatusLabel, submissionStatusTone } from '@/lib/submissions';

/**
 * One submission tile in a competitor/staff grid. Owns its own rename
 * mutation (via a per-instance hook) so each card in the grid can be edited
 * independently without the parent list managing per-row mutation state.
 */
export function SubmissionCard({
  submission: s,
  averageScore,
  canRename,
  canModerate,
  onAccept,
  onReject,
  isAccepting,
  isRejecting,
}: {
  submission: Submission;
  /** Average judge score; only passed once judging is complete and the viewer may see it. */
  averageScore?: number;
  /** Own submission, not yet Accepted/Rejected - mirrors Submission.UpdateDetails' own status gate. */
  canRename: boolean;
  canModerate: boolean;
  onAccept: () => void;
  onReject: () => void;
  isAccepting: boolean;
  isRejecting: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(s.title);
  const updateSubmission = useUpdateSubmission(s.id);

  function startEditing() {
    setTitle(s.title);
    setIsEditing(true);
  }

  async function handleSave() {
    if (!title.trim()) return;
    try {
      await updateSubmission.mutateAsync({ title, description: s.description });
      setIsEditing(false);
    } catch {
      // Error surfaced inline below via updateSubmission.error - stay in edit mode.
    }
  }

  return (
    <Card className="group h-full overflow-hidden">
      <Link to={`/submissions/${s.id}`}>
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-surface-3">
          <SubmissionThumbnail submissionId={s.id} alt={s.title} />
          {averageScore !== undefined && (
            <span className="absolute right-2 top-2 rounded-md bg-scrim px-2 py-0.5 text-xs font-semibold text-fg tabular-nums">
              {averageScore.toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-scrim opacity-0 transition-opacity duration-fast group-hover:opacity-100" />
        </div>
      </Link>
      <CardBody className="flex flex-col gap-2">
        {isEditing ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Input
                aria-label="Naslov prispevka"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                isLoading={updateSubmission.isPending}
                onClick={handleSave}
                aria-label="Shrani ime"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(false)}
                aria-label="Prekliči"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {updateSubmission.isError && (
              <span className="text-xs text-danger">{getApiErrorMessage(updateSubmission.error)}</span>
            )}
          </div>
        ) : (
          <Link to={`/submissions/${s.id}`} className="flex items-center justify-between gap-2">
            <h3 className="truncate text-sm font-medium text-fg">{s.title}</h3>
            <Badge tone={submissionStatusTone(s.status)}>{submissionStatusLabel(s.status)}</Badge>
          </Link>
        )}
      </CardBody>
      {canRename && !isEditing && (
        <CardBody className="flex gap-2 border-t border-line pt-3">
          <Button type="button" size="sm" variant="secondary" onClick={startEditing}>
            <Pencil className="h-3.5 w-3.5" />
            Preimenuj
          </Button>
        </CardBody>
      )}
      {canModerate && s.status === SubmissionStatus.Submitted && (
        <CardBody className="flex gap-2 border-t border-line pt-3">
          <Button type="button" variant="secondary" size="sm" isLoading={isAccepting} onClick={onAccept}>
            Sprejmi
          </Button>
          <Button type="button" variant="danger" size="sm" isLoading={isRejecting} onClick={onReject}>
            Zavrni
          </Button>
        </CardBody>
      )}
    </Card>
  );
}
